<script setup lang="ts">
  import { ref } from 'vue';
  import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@/shared/ui';

  // Required for "Decimal", but choosing that option must not greet the author with an error: the
  // message waits until the field is typed in or left. The field mounts with the option, so picking
  // "Decimal" again starts untouched.
  const touched = ref(false);
  const MAX_LENGTH = 2;

  function keepDigits(event: Event, handleChange: (value: string) => void) {
    const target = event.target as HTMLInputElement;
    target.value = target.value.replace(/\D/g, '');
    touched.value = true;
    handleChange(target.value);
  }

  // The shared Input keeps its own copy of the typed text, so a character stripped on input comes
  // back on its next render; a non-digit has to be stopped before it reaches the field.
  function blockNonDigit(event: KeyboardEvent) {
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    if (!/\d/.test(event.key)) event.preventDefault();
  }

  function pasteDigits(event: ClipboardEvent, handleChange: (value: string) => void) {
    event.preventDefault();
    const target = event.target as HTMLInputElement;
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const next = `${target.value.slice(0, start)}${digits}${target.value.slice(end)}`.slice(0, MAX_LENGTH);
    target.value = next;
    touched.value = true;
    handleChange(next);
  }

  function leave(event: FocusEvent, onBlur: (event: FocusEvent) => void) {
    touched.value = true;
    onBlur(event);
  }
</script>

<template>
  <FormField
    v-slot="{ componentField, handleChange }"
    name="decimalPlaces"
  >
    <FormItem class="flex flex-col gap-2">
      <FormLabel class="text-sm text-gray-11 font-normal">
        Quantidade de casas decimais
      </FormLabel>
      <FormControl>
        <Input
          :model-value="componentField.modelValue"
          :name="componentField.name"
          type="text"
          inputmode="numeric"
          placeholder="Ex: 2"
          class="h-11 border-gray-02 text-gray-07"
          :maxlength="MAX_LENGTH"
          @keydown="blockNonDigit"
          @paste="pasteDigits($event, handleChange)"
          @blur="leave($event, componentField.onBlur)"
          @input="keepDigits($event, handleChange)"
        />
      </FormControl>
      <FormMessage v-if="touched" />
    </FormItem>
  </FormField>
</template>
